import React, { useState, useEffect, useMemo } from "react";

// Importações do TanStack Table e React-Bootstrap
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    flexRender
} from "@tanstack/react-table";
import { pega_ranking_soma_bolsa, pega_matriz_curricular } from "../../services/api";
import { ConverteSerie } from "./modulo";
import { Container, Nav, Navbar, NavDropdown, Table, Form } from 'react-bootstrap';

// --- HELPERS E COMPONENTES DE FILTRO ---

// Mapa para definir a ordem correta das séries (fora do componente principal)
const serieSortOrder = new Map([
    ['1º Ano EF', 1], ['2º Ano EF', 2], ['3º Ano EF', 3], ['4º Ano EF', 4],
    ['5º Ano EF', 5], ['6º Ano EF', 6], ['7º Ano EF', 7], ['8º Ano EF', 8],
    ['9º Ano EF', 9], ['1ª Série EM', 10], ['2ª Série EM', 11], ['3ª Série EM', 12],
    ['Pré-Vestibular', 13],
]);

// Função de ordenação customizada para a coluna de série
const serieSortingFn = (rowA, rowB, columnId) => {
    const orderA = serieSortOrder.get(rowA.getValue(columnId)) || 0;
    const orderB = serieSortOrder.get(rowB.getValue(columnId)) || 0;
    return orderA - orderB;
};

// Componente de Filtro de Seleção para Unidades
const SelectColumnFilter = ({ column }) => {
    const facetedValues = column.getFacetedUniqueValues();
    const sortedUniqueValues = useMemo(
        () => Array.from(facetedValues.keys()).sort(),
        [facetedValues]
    );

    return (
        <Form.Select
            value={column.getFilterValue() || ''}
            onChange={e => column.setFilterValue(e.target.value || undefined)}
            onClick={e => e.stopPropagation()}
            size="sm"
            className="mt-1"
        >
            <option value="">Todas</option>
            {sortedUniqueValues.map(value => (
                <option key={value} value={value}>
                    {value} ({facetedValues.get(value)})
                </option>
            ))}
        </Form.Select>
    );
};

// Componente de Filtro Numérico para Ranking ("Top N")
const RankFilter = ({ column }) => {
    const { getFilterValue, setFilterValue } = column;

    return (
        <Form.Control
            type="number"
            value={(getFilterValue() ?? '')}
            onChange={e => setFilterValue(e.target.value ? Number(e.target.value) : undefined)}
            placeholder={`Ver até o rank...`}
            size="sm"
            className="mt-1"
        />
    );
};

// Componente para a UI do seletor de ciclos
const CycleSelector = ({ selected, onChange }) => {
    const handleCycleChange = (cycle) => {
        onChange(prev => ({ ...prev, [cycle]: !prev[cycle] }));
    };

    return (
        <div className="mb-3 p-3 border rounded bg-light">
            <h5 className="mb-2">Considerar no Cálculo do Desempenho:</h5>
            <Form>
                {Object.keys(selected).map((cycle, index) => (
                    <Form.Check
                        key={cycle}
                        inline
                        type="checkbox"
                        id={`cycle-${cycle}`}
                        label={`Ciclo ${index + 1}`}
                        checked={selected[cycle]}
                        onChange={() => handleCycleChange(cycle)}
                    />
                ))}
            </Form>
        </div>
    );
};


// --- COMPONENTE PRINCIPAL ---
const Ranking_Series_Unidade = (props) => {
    const [data2, setData2] = useState([]);
    const [matrizCurricular, setMatrizCurricular] = useState([]);
    const unidades = ['', 'Bento Ribeiro', 'Madureira', 'Santa Cruz', 'Cascadura', 'Taquara', 'Nilopolis', '', '', 'Seropédica', 'Barra da Tijuca', 'Campo Grande', 'Centro de Excelência', 'Mangueira', 'Maricá', 'Ilha do Governador', 'Freguesia', 'Recreio dos Bandeirantes'];
    const perfil = JSON.parse(localStorage.getItem("perfil"));
    const [selectedCycles, setSelectedCycles] = useState({ c1: true, c2: true, c3: true, c4: false });

    // Busca de dados
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await pega_ranking_soma_bolsa();
                const response2 = await pega_matriz_curricular();
                setData2(response);
                setMatrizCurricular(response2);
            } catch (error) {
                console.error("Erro ao buscar dados:", error);
            }
        };
        fetchData();
    }, []);

    // Memoização da pipeline de dados
    const processedData = useMemo(() => {
        if (!data2.length || !matrizCurricular.length) return [];
        const activeCycles = Object.keys(selectedCycles).filter(c => selectedCycles[c]);
        const numActiveCycles = activeCycles.length;

        if (numActiveCycles === 0) {
            return data2.map(item => ({
                unidade: unidades[parseInt(item.unidade)], serie: ConverteSerie(item.serie), desempenhoCalculado: 0,
            }));
        }

        return data2.map(item => {
            let _ser = item.tur.substring(2, 3);
            let _gra = item.tur.substring(0, 1);
            let divisor = matrizCurricular.filter(mc => mc.ser === _ser && mc.gra === _gra && mc.sts === true && (parseInt(mc.dis) < 340 || parseInt(mc.dis) > 357)).length;
            if (divisor === 0) return { unidade: unidades[parseInt(item.unidade)], serie: ConverteSerie(item.serie), desempenhoCalculado: 0 };

            const totalPontos = activeCycles.reduce((sum, cycleKey) => sum + parseFloat(item[cycleKey]), 0);
            const totalPontosPossiveis = divisor * 25 * numActiveCycles;
            const desempenhoCalculado = parseFloat(((totalPontos / totalPontosPossiveis) * 100).toFixed(2));

            return {
                unidade: unidades[parseInt(item.unidade)],
                serie: ConverteSerie(item.serie),
                desempenhoCalculado: isNaN(desempenhoCalculado) ? 0 : desempenhoCalculado,
            };
        });
    }, [data2, matrizCurricular, selectedCycles]);

    const aggregatedData = useMemo(() => {
        if (!processedData.length) return [];
        const seriesMap = new Map();
        processedData.forEach(aluno => {
            const key = `${aluno.unidade}|${aluno.serie}`;
            if (!seriesMap.has(key)) {
                seriesMap.set(key, {
                    unidade: aluno.unidade, serie: aluno.serie, totalDesempenho: 0, count: 0,
                });
            }
            const serieData = seriesMap.get(key);
            serieData.totalDesempenho += aluno.desempenhoCalculado;
            serieData.count += 1;
        });
        return Array.from(seriesMap.values()).map(serie => ({
            unidade: serie.unidade,
            serie: serie.serie,
            desempenhoCalculado: parseFloat((serie.totalDesempenho / serie.count).toFixed(2)),
        }));
    }, [processedData]);

    const rankedData = useMemo(() => {
        if (!aggregatedData.length) return [];
        const seriesGrouped = new Map();
        aggregatedData.forEach(item => {
            if (!seriesGrouped.has(item.serie)) seriesGrouped.set(item.serie, []);
            seriesGrouped.get(item.serie).push(item);
        });
        const finalRankedData = [];
        seriesGrouped.forEach((seriesArray) => {
            const sorted = seriesArray.sort((a, b) => b.desempenhoCalculado - a.desempenhoCalculado);
            finalRankedData.push(...sorted.map((item, index) => ({
                ...item, rank: index + 1, totalInRank: sorted.length,
            })));
        });
        return finalRankedData;
    }, [aggregatedData]);

    // Definição das colunas da tabela
    const columns = useMemo(() => [
        {
            accessorKey: 'rank',
            header: 'Rank (Rede)',
            cell: info => {
                const rank = info.getValue();
                const total = info.row.original.totalInRank;
                return <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{`${rank}º de ${total}`}</span>;
            },
            enableColumnFilter: true,
            filterFn: (row, columnId, filterValue) => {
                if (filterValue === undefined) return true;
                return row.getValue(columnId) <= filterValue;
            },
            Filter: ({ column }) => <RankFilter column={column} />,
        },
        {
            accessorKey: 'unidade',
            header: 'Unidade',
            enableColumnFilter: true,
            filterFn: 'equals',
            Filter: ({ column }) => <SelectColumnFilter column={column} />,
        },
        {
            accessorKey: 'serie',
            header: 'Série',
            enableColumnFilter: false,
            sortingFn: serieSortingFn,
        },
        {
            accessorKey: 'desempenhoCalculado',
            header: 'Desempenho (Calculado)',
            cell: info => {
                const value = info.getValue();
                let style = {};
                if (value < 25) style = { backgroundColor: '#f8d7da', color: '#721c24' };
                else if (value < 50) style = { backgroundColor: '#ffeeba', color: '#856404' };
                else if (value < 70) style = { backgroundColor: '#fff3cd', color: '#856404' };
                else if (value < 90) style = { backgroundColor: '#d4edda', color: '#155724' };
                else style = { backgroundColor: '#cce5ff', color: '#004085' };
                return <div style={{ ...style, padding: '5px', borderRadius: '4px', textAlign: 'center', fontWeight: 'bold' }}>{value}%</div>;
            },
        }
    ], []);

    // Instância da tabela
    const table = useReactTable({
        data: rankedData,
        columns,
        initialState: {
            sorting: [{ id: 'serie', desc: false }],
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    // Função para renderizar o dropdown de unidades baseado no perfil
    const Experiment = (Nperfil) => {
        if (Nperfil === "GESTOR")
            return (
                <>
                    <NavDropdown.Item href="/unidade/10">Barra da Tijuca</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/01">Bento Ribeiro</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/11">Campo Grande</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/04">Cascadura</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/16">Freguesia</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/15">Ilha do Governador</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/02">Madureira</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/14">Maricá</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/06">Nilópolis</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/17">Recreio</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/03">Santa Cruz</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/09">Seropédica</NavDropdown.Item>
                    <NavDropdown.Item href="/unidade/05">Taquara</NavDropdown.Item>
                </>
            );
        if (Nperfil?.startsWith("DIRETOR") || Nperfil?.startsWith("SUPERVISOR")) {
            const numerosString = Nperfil.replace("DIRETOR", "").replace("SUPERVISOR", "").trim();
            const numerosArray = numerosString.split("-");
            return numerosArray.map(function (cod_unidade) {
                return (
                    <NavDropdown.Item href={`/unidade/${cod_unidade}`} key={cod_unidade}>
                        {unidades[parseInt(cod_unidade)]}
                    </NavDropdown.Item>
                );
            });
        }
        return null;
    };

    // Renderização do componente
    return (
        <div className="Evasão">
            <div className="sticky-top painel">
                <Navbar expand="lg" className="bg-body-tertiary">
                    <Navbar.Brand href="/">
                        <img src="/novo-logo.png" alt="Santa Mônica Rede de Ensino" style={{ width: "40px", height: "40px", marginRight: "10px" }} />
                        <span style={{ color: "#006cb5", fontFamily: "inherit", fontWeight: "bold" }}>Santa Mônica Rede de Ensino</span>
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav">
                        <NavDropdown title="UNIDADES">
                            {perfil && Experiment(perfil[0].perfil)}
                        </NavDropdown>
                        <Nav.Link href={`/ranking`}>RANKING DE ALUNOS</Nav.Link>
                        <Nav.Link href={`/ranking-series`} active>RANKING DE SÉRIES</Nav.Link>
                    </Navbar.Collapse>
                </Navbar>
            </div>

            <Container fluid className="mt-4">
                <h1 className="text-center mb-4">Ranking de Desempenho das Séries por Unidade</h1>

                <CycleSelector selected={selectedCycles} onChange={setSelectedCycles} />

                <Table striped bordered hover responsive="md" className="shadow-sm mt-3">
                    <thead className="table-primary">
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th key={header.id} style={{ verticalAlign: 'middle' }}>
                                        <div
                                            onClick={header.column.getToggleSortingHandler()}
                                            className={header.column.getCanSort() ? 'cursor-pointer user-select-none' : ''}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {{ asc: ' 🔼', desc: ' 🔽' }[header.column.getIsSorted()] ?? null}
                                        </div>
                                        {header.column.getCanFilter() ? (
                                            <div>
                                                {flexRender(header.column.columnDef.Filter, { column: header.column })}
                                            </div>
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} style={{ verticalAlign: 'middle' }}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Container>
        </div>
    );
};

export default Ranking_Series_Unidade;